// libraries
import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import FocusTrap from 'focus-trap-react'

// components
import ListProductCategories from '/react4xp/components/ListProductCategories'
import Section from '/react4xp/components/Section'
import RichText from '/react4xp/components/RichText'
import ProductCard from '/react4xp/components/ProductCard'
import Checkbox from '/react4xp/components/Form/Simple/Checkbox'

// svg
import { Download, Close } from '/react4xp/components/Svg/Ux/Ux'
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'
import { PadLock } from '/react4xp/components/Svg/Others/Icons'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'
  
// interface
interface GroupedCategory {
	title: string
	description?: string
	products?: ProductCategoryPageProps['products']
	subCategories?: Array<{
		title: string
		description?: string
		products?: ProductCategoryPageProps['products']
	}>
}

export interface ProductCategoryPageProps {
	generalInformationPopup?: {
		title: string
		description: string
		list: Array<{
			type: string
			name: string
			size: string
			href: string
			isRestricted?: boolean
		}>
	}
	topInformation: {
		icon: string
		title: string
		description: string
		button?: string
	}
	filters: {
		mobileTitle: string
		viewAllText: string
		categories: Array<{
			title: string
			description?: string
			subCategories?: Array<{
				title: string
				description?: string
			}>
		}>
		historicProducts: {
			checkboxText: string
			title: string
			description?: string
		}
	}
	products: Array<{
		href: {
			url: string
			isExternal?: boolean
		}
		img: {
			src: string
			alt?: string
		}
		name: string
		partNumber?: string
		category: string
		subCategory?: string
		historicProduct: boolean
	}>
	noProductsFound: string
}
  
export default function ProductCategoryPage({
	generalInformationPopup,
	topInformation,
	filters,
	products,
	noProductsFound
}: ProductCategoryPageProps) {
  
	// add body class
	useEffect(() => {
		document.body.classList.add('product-category')
	}, [])

	// filter functions
	const [mobileFilters, setMobileFilters] = useState(false)
	const [seeAllChecked, setSeeAllChecked] = useState(true)
	const [historicProducts, setHistoricProducts] = useState(false)

	const [categoryChecked, setCategoryChecked] = useState(
		filters?.categories.map(() => false)
	)
	const [subCategoryChecked, setSubCategoryChecked] = useState(
		filters?.categories.map((item) => item.subCategories?.map(() => false) || [])
	)

	// handle "see all products" checkbox
	const handleSeeAllProducts = (e) => {
		const isChecked = e.target.checked
		setSeeAllChecked(isChecked)
		setHistoricProducts(false)

		// if "see all products" is checked, uncheck all category and subcategory checkboxes
		if (isChecked) {
			setCategoryChecked(filters?.categories.map(() => false))
			setSubCategoryChecked(
				filters?.categories.map((item) => item.subCategories?.map(() => false) || [])
			)
		}
	}

	// bind a function on click to close the dropdown if clicked outside
	const mobileFiltersRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (mobileFiltersRef.current && !mobileFiltersRef.current.contains(event.target)) {
				setMobileFilters(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	// handle individual category checkbox
	const handleCategoryChange = (index) => {
		const updatedCategories = [...categoryChecked]
		const isCategoryChecked = !updatedCategories[index]
		updatedCategories[index] = isCategoryChecked
		setCategoryChecked(updatedCategories)
		setHistoricProducts(false)

		// if a category has subcategories and is checked, check all subcategories
		if (filters?.categories[index].subCategories) {
			const updatedSubCategories = [...subCategoryChecked]
			updatedSubCategories[index] = updatedSubCategories[index].map(() => isCategoryChecked)
			setSubCategoryChecked(updatedSubCategories)
		}

		// if any category checkbox is checked, uncheck "see all products"
		if (updatedCategories.some((checked) => checked)) {
			setSeeAllChecked(false)
		}
	}

	// handle individual subcategory checkbox
	const handleSubCategoryChange = (categoryIndex, subCategoryIndex) => {
		const updatedSubCategories = [...subCategoryChecked]
		updatedSubCategories[categoryIndex][subCategoryIndex] = !updatedSubCategories[categoryIndex][subCategoryIndex]
		setSubCategoryChecked(updatedSubCategories)
		setHistoricProducts(false)
	}

	// handle historic products
	const handleHistoricProducts = () => {
		setHistoricProducts((prevState) => !prevState)
		setSeeAllChecked(false)
		setCategoryChecked(filters?.categories.map(() => false))
		setSubCategoryChecked(
			filters?.categories.map((item) => item.subCategories?.map(() => false) || [])
		)
	}
  
	// filter products based on selected categories and subcategories
	const filteredProducts = seeAllChecked
		? products.filter(product => !product.historicProduct)
		: historicProducts 
			? products.filter(product => product.historicProduct === true) 
			: products.filter((product) => {
				const categoryIndex = filters?.categories.findIndex(
					(cat) => cat.title === product.category
				)

				if (categoryIndex === -1 || !categoryChecked[categoryIndex]) return false

				if (product.historicProduct) return false

				if (product.subCategory) {
					const subCategoryIndex = filters?.categories[categoryIndex].subCategories?.findIndex(
						(subCat) => subCat.title === product.subCategory
					)
					return subCategoryIndex !== -1 && subCategoryChecked[categoryIndex][subCategoryIndex]
				}

				return true
	})
  
	const groupedProducts = (seeAllChecked ? [] : filters?.categories.map((category, categoryIndex) => {
		if (!categoryChecked[categoryIndex]) return null
	
			const productsInCategory = filteredProducts.filter(
				(product) => product.category === category.title
			)

			if (!category.subCategories) {
				return productsInCategory.length > 0
					? { ...category, products: productsInCategory }
					: null
			}

			const topLevelProducts = productsInCategory.filter(
				(product) => !product.subCategory
			)

			const subCategoriesWithProducts = category.subCategories.map((subCategory, subCategoryIndex) => {
				if (!subCategoryChecked[categoryIndex][subCategoryIndex]) {
					return null
				}

				const productsInSubCategory = productsInCategory.filter(
					(product) => product.subCategory === subCategory.title
				)

				return productsInSubCategory.length > 0
					? { ...subCategory, products: productsInSubCategory }
					: null
			}).filter(Boolean)

			if (subCategoriesWithProducts.length > 0 || topLevelProducts.length > 0) {
				return {
					title: category.title,
					description: category.description,
					...(subCategoriesWithProducts.length > 0 ? { subCategories: subCategoriesWithProducts } : {} ),
					...(topLevelProducts.length > 0 ? { products: topLevelProducts } : {})
				}
			}

			return null
		}).filter(Boolean)

	) as GroupedCategory[]
  
	// general information drawer
	const [isGeneralInformationVisible, setIsGeneralInformationVisible] = useState(false)
	const [focusTrapped, setFocusTrapped] = useState(false)

	// open the general information drawer
	const openGeneralInformation = () => {
		setIsGeneralInformationVisible(true)

		const mainElement = document.querySelector<HTMLElement>('main#main')
		if (mainElement) {
			mainElement.style.zIndex = 'auto'
		}

		setTimeout(() => {
			setFocusTrapped(true)
		}, 150)

		setTimeout(() => {
			const wrapper = document.querySelector('.general-information-drawer') as HTMLElement
			wrapper.style.transitionDelay = '.3s'
		}, 300)
	}
  
	// closes the general information drawer
	const closeGeneralInformation = () => {
		setIsGeneralInformationVisible(false)
		setFocusTrapped(false)

		const mainElement = document.querySelector<HTMLElement>('main#main')
		
		if (mainElement) {
			setTimeout(() => (
				mainElement.style.zIndex = '4'
			), 300)
		}

		setTimeout(() => {
			const wrapper = document.querySelector('.general-information-drawer') as HTMLElement
			wrapper.style.transitionDelay = '0s'
		}, 400)
	}

	// add 'no-scroll' class to body when the general information drawer is open
	// and enable closing it with ESC
	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeGeneralInformation()
			}
		}

		if (isGeneralInformationVisible) {
			document.addEventListener('keydown', handleEscape)
			document.body.classList.add('no-scroll')
		} else {
			document.body.classList.remove('no-scroll')
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isGeneralInformationVisible])
  
	// make the "see all products" checked if everything else is unchecked
	useEffect(() => {
		const areAllUnchecked =
			categoryChecked.every((checked) => !checked) &&
			subCategoryChecked.every((subArray) => subArray.every((checked) => !checked)) &&
			!historicProducts

		if (areAllUnchecked) {
			setSeeAllChecked(true)
		}
	}, [categoryChecked, subCategoryChecked])

	// tell typescript that a category has products
	const hasProducts = (
		category: any
	): category is { products: ProductCategoryPageProps['products'] } => {
		return 'products' in category && Array.isArray(category.products)
	}
  
	return (
		<div className='page--product-category'>
			{generalInformationPopup && (
				<FocusTrap
					active={focusTrapped}
					focusTrapOptions={{
						clickOutsideDeactivates: true
					}}
				>
					<div
						className={clsx(
							'general-information-drawer',
							isGeneralInformationVisible && 'active'
						)}
						style={{
							opacity: '0',
							position: 'fixed'
						}}
					>
						<div className='general-information-drawer__wrapper'>
							<button
								className='general-information-drawer__close'
								onClick={closeGeneralInformation}
								aria-label='Close'
							>
								<Close />
							</button>

							<div className='general-information-drawer__content'>
								<div className='content-top'>

									{isNonWhitespaceString(generalInformationPopup?.title) && (
										<p className='title-xs title'>
											{generalInformationPopup?.title}
										</p>
									)}

									{isNonWhitespaceString(generalInformationPopup?.description) && (
										<p className='desc'>
											{generalInformationPopup?.description}
										</p>
									)}

								</div>

								<div className='content-bottom'>
									{generalInformationPopup?.list?.map((item, i) => (
										<a
											href={item.href}
											className='content-bottom__item'
											key={i}
											download={!item.isRestricted}
										>
											<span className='infos'>

												<p className='name'>
													{item.name}
												</p>

												<span className='infos-bottom'>

													{isNonWhitespaceString(item.size) && (
														<p>
															{item.size}
														</p>
													)}

													{isNonWhitespaceString(item.type) && (
														<p>
															{item.type}
														</p>
													)}

												</span>
											</span>

											<span className='icon' arial-label={item.isRestricted ? 'Restricted' : 'Download'}>
												{item.isRestricted ? (
													<PadLock />
												) : (
													<Download />
												)}
											</span>

										</a>
									))}
								</div>
							</div>
						</div>

						<div
							className='general-information-drawer__bg'
							onClick={closeGeneralInformation}
						></div>

					</div>
				</FocusTrap>
			)}

			{topInformation && (
				<ListProductCategories
					categories={[
						{
							icon: topInformation.icon,
							title: topInformation.title,
							description: topInformation.description,
							cta:
								generalInformationPopup?.list?.length > 0 && {
									label: topInformation.button,
									onClick: () => openGeneralInformation()
								}
						}
					]}
				/>
			)}

			<Section bgColor='bg-beige-050' textColor='text-dark' className='products-list'>
				<div className='grid-container'>
					<div className='grid-row main-grid-row'>
						
						<div className='c-md-3 products-list__filters'>
							{filters && (
								<div
									className={clsx(
										'all-filters',
										mobileFilters && 'open'
									)}
									ref={mobileFiltersRef}
								>

									<button
										className='mobile-title'
										onClick={() => setMobileFilters(!mobileFilters)}
									>

										<span className='text'>
											{filters.mobileTitle}
										</span>

										<span className='icon'>
											<AngleDown />
										</span>

									</button>

									<div className='filter-wrapper'>
										<div>
											<div>

												<Checkbox
													checked={seeAllChecked}
													onChange={handleSeeAllProducts}
												>
													{filters.viewAllText}
												</Checkbox>

												{filters.categories?.map((item, i) => (
													<div className='filter-line' key={i}>
														<Checkbox
															checked={categoryChecked[i]}
															onChange={() => handleCategoryChange(i)}
														>
															{item.title}
														</Checkbox>

														{item.subCategories && (
															<div className='sub-filters'>
																{item.subCategories.map((subItem, i2) => (
																	<Checkbox
																		key={i2}
																		checked={subCategoryChecked[i][i2]}
																		onChange={() =>
																			handleSubCategoryChange(i, i2)
																		}
																	>
																		{subItem.title}
																	</Checkbox>
																))}
															</div>
														)}
													</div>
												))}

												{filters?.historicProducts && (
													<Checkbox
														checked={historicProducts}
														onChange={() => handleHistoricProducts()}
													>
														{filters.historicProducts.checkboxText}
													</Checkbox>
												)}

											</div>
										</div>
									</div>
								</div>
							)}
						</div>

						<div className='c-md-9 products-list__grid'>
							{seeAllChecked && filteredProducts.length > 0 && (
								// display only products when "see all products" is checked
								<div className='grid-row'>
									{filteredProducts.length > 0 ? (
										filteredProducts.map((item, i) => (
											<div className='c-6 c-lg-4' key={i}>
												<ProductCard
													href={{
														url: item.href?.url,
														isExternal: item.href?.isExternal
													}}
													img={{
														src: item.img?.src,
														alt: item.img?.alt
													}}
													name={item.name}
													partNumber={item.partNumber}
												/>
											</div>
										))
									) : (
										<div className='no-products-found'>
											<p className='title-sm'>{noProductsFound}</p>
										</div>
									)}
								</div>
							)}

							{groupedProducts.length > 0 && ( groupedProducts.map((category, i) => (
								<div
									key={i}
									className={clsx(
										'category-section',
										category.subCategories?.length &&
											'has-subcategories'
									)}
								>
									
									{(category.products?.length || 0) > 0 && 
										(category.title || category.description) && (
											<div className='category-header'>

												{category.title && (
													<h2 className='title-sm category-title'>
														{category.title}
													</h2>
												)}

												{category.description && (
													<div className='category-desc'>
														<RichText content={category.description} />
													</div>
												)}
												
											</div>
										)
									}

									{hasProducts(category) && category.products.length > 0 && (
										<div className='category-products'>
											<div className='grid-row'>
												{category.products.map((product, i2) => (
													<div className='c-6 c-lg-4' key={i2}>
														<ProductCard
															href={{
																url: product.href?.url,
																isExternal: product.href?.isExternal
															}}
															img={{
																src: product.img?.src,
																alt: product.img?.alt
															}}
															name={product.name}
															partNumber={product.partNumber}
														/>
													</div>
												))}
											</div>
										</div>
									)}

									{category.subCategories && category.subCategories.map((subCategory, i2) => subCategory ? (
										<div key={i2} className='subcategory-section'>

											{(subCategory.title || subCategory.description) && (
												<div className='category-header'>

													{subCategory.title && (
														<h3 className='title-sm category-title'>
															{subCategory.title}
														</h3>
													)}

													{subCategory.description && (
														<div className='category-desc'>
															<RichText
																content={subCategory.description}
															/>
														</div>
													)}

												</div>
											)}

											<div className='subcategory-products'>
												<div className='grid-row'>
													{subCategory?.products?.map((product, i3) => (
														<div
															className='c-6 c-lg-4'
															key={i3}
														>
															<ProductCard
																href={{
																	url: product.href?.url,
																	isExternal:
																		product.href?.isExternal
																}}
																img={{
																	src: product.img?.src,
																	alt: product.img?.alt
																}}
																name={product.name}
																partNumber={
																	product.partNumber
																}
															/>
														</div>
													))}
												</div>
											</div>

										</div>
									) : null )}

								</div>
							)))}

							{filteredProducts.length === 0 && (
								<div className='no-products-found'>
									<p className='title-sm'>
										{noProductsFound}
									</p>
								</div>
							)}

							{historicProducts && filteredProducts.length > 0 && (
								<div className='category-section'>
									{(filters.historicProducts?.title || filters.historicProducts?.description) && (
										<div className='category-header'>

											{filters.historicProducts?.title && (
												<h2 className='title-sm category-title'>
													{filters.historicProducts?.title}
												</h2>
											)}

											{filters.historicProducts?.description && (
												<div className='category-desc'>
													<RichText
														content={filters.historicProducts?.description}
													/>
												</div>
											)}

										</div>
									)}

									<div className='category-products'>
										<div className='grid-row'>
											{filteredProducts.map((item, i) => (
												<div className='c-6 c-lg-4' key={i}>
													<ProductCard
														href={{
															url: item.href?.url,
															isExternal: item.href?.isExternal
														}}
														img={{
															src: item.img?.src,
															alt: item.img?.alt
														}}
														name={item.name}
														partNumber={item.partNumber}
													/>
												</div>
											))}
										</div>
									</div>

								</div>
							)}

						</div>

					</div>
				</div>
			</Section>

		</div>
	)
}  